import { DownOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { FC, memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Language, Product } from 'types/schema';
import calculateVAT from 'utils/calculateVAT';
import formatMoney from 'utils/formatMoney';
import utilMessages from 'utils/messages';
import './ProductPrice.css';

export const PriceContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 4px;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0 4px;
`;

const DiscountPrice = styled.p`
  font-size: 24px;
  font-weight: 500;
  line-height: 26px;
  color: #3c9041;

  @media (min-width: 1024px) {
    font-size: 28px;
  }
`;

const BasePrice = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #596056;
  text-decoration: line-through;
  line-height: 18px;
`;

const PerPrice = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  cursor: pointer;
  color: #808289;
`;

const StyledDownIcon = styled(DownOutlined)`
  font-size: 10px;
  margin-left: 2px;
  cursor: pointer;
`;

const TooltipList = styled.ul`
  padding: 0;
`;
const VAT = styled.p`
  font-family: 'Inter';
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: #757d73;
  margin-top: 4px;
  font-style: italic;
`;
interface IProductPrice {
  product: Product;
  uom: string;
  baseUom: string;
  netUom: string;
  lang: Language;
}

interface TooltipContentProps {
  uom: string;
  baseUomPrice: number | null | undefined;
  netUomPrice: number | null | undefined;
  netUom: string;
  lang: Language;
}

interface TooltipUnitPriceProps extends Omit<TooltipContentProps, 'uom'> {
  baseUom: string;
}

interface UnitPriceDisplayProps {
  price: number | null | undefined;
  unit: string;
}

const PRICE_TEXT = {
  [Language.Vi]: 'Giá',
  [Language.En]: 'Price',
};

const formatPrice = (price: number | null | undefined): string => {
  return `${formatMoney(price || 0, 1)}đ`;
};

export const formatUnitPrice = (price: number | null | undefined, unit?: string): string => {
  return `${formatPrice(price)}${unit ? `/${unit}` : ''}`;
};

const TooltipContent = ({ uom, baseUomPrice, netUom, netUomPrice, lang }: TooltipContentProps) => (
  <TooltipList>
    <li>
      {PRICE_TEXT[lang]}/{uom}: {formatUnitPrice(baseUomPrice, uom)}
    </li>
    {netUomPrice && (
      <li>
        {PRICE_TEXT[lang]}/{netUom}: {formatUnitPrice(netUomPrice, netUom)}
      </li>
    )}
  </TooltipList>
);

const UnitPriceDisplay = ({ price, unit }: UnitPriceDisplayProps) => (
  <PerPrice>({formatUnitPrice(price, unit)})</PerPrice>
);

const TooltipUnitPrice = ({ baseUomPrice, baseUom, netUomPrice, netUom, lang }: TooltipUnitPriceProps) => {
  const tooltipProps = {
    uom: baseUom,
    baseUomPrice: baseUomPrice,
    netUom: netUom,
    netUomPrice: netUomPrice,
    lang: lang,
  };

  return (
    <Tooltip
      overlayClassName="price-tooltip-container"
      arrow={false}
      placement="bottomLeft"
      title={<TooltipContent {...tooltipProps} />}
    >
      <PerPrice>
        ({formatUnitPrice(baseUomPrice, baseUom)}
        <StyledDownIcon />)
      </PerPrice>
    </Tooltip>
  );
};

const ProductPrice: FC<IProductPrice> = ({ product, uom, baseUom, netUom, lang }) => {
  const { netUnit, baseUnit, baseUomPrice, netUomPrice, priceV2 } = product;

  const renderUomPrice = useMemo(() => {
    if (baseUomPrice && netUomPrice && baseUnit !== 1) {
      return (
        <TooltipUnitPrice
          baseUomPrice={calculateVAT(baseUomPrice, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat}
          baseUom={baseUom}
          netUomPrice={calculateVAT(netUomPrice, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat}
          netUom={netUom}
          lang={lang}
        />
      );
    }
    if (baseUomPrice && baseUnit !== 1) {
      return (
        <UnitPriceDisplay
          price={calculateVAT(baseUomPrice, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat}
          unit={baseUom}
        />
      );
    }
    if (netUomPrice && netUnit !== 1) {
      return (
        <UnitPriceDisplay
          price={calculateVAT(netUomPrice, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat}
          unit={netUom}
        />
      );
    }
    return null;
  }, [baseUnit, netUnit, netUomPrice, baseUomPrice, netUom, baseUom, lang]);

  return (
    <>
      <PriceContainer>
        <PriceRow>
          <DiscountPrice>
            {formatMoney(calculateVAT(priceV2?.price || 0, priceV2?.vat || 0, !!priceV2?.vatIncluded).beforeVat)}đ
          </DiscountPrice>
          {!!priceV2?.originalPrice && priceV2?.originalPrice > priceV2?.price && (
            <BasePrice>
              {formatPrice(calculateVAT(priceV2.originalPrice, priceV2?.vat, priceV2?.vatIncluded).beforeVat)}
            </BasePrice>
          )}
        </PriceRow>
        {renderUomPrice}
      </PriceContainer>
      <VAT>
        (
        <FormattedMessage
          {...utilMessages.priceIncludedVat}
          values={{
            price: `${formatMoney(calculateVAT(priceV2?.price || 0, priceV2?.vat || 0, !!priceV2?.vatIncluded).afterVat)}đ`,
          }}
        />
        )
      </VAT>
    </>
  );
};

export default memo(ProductPrice);
